/**
 * @fileoverview View menu component for companies list
 * @active View menu component for companies list at /companies
 * 
 * This component provides a dropdown menu for selecting views and adding new views
 * It also includes a temporary waitlist CTA button for non-logged in users
 * 
 */
import { memo, useState } from "react";

import {
  Box,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  useDisclosure,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";

import ArrowDownSvg from "@/assets/svg/arrow-down-16x16.svg";
import ListIcon from "@/assets/svg/new-list-icon.svg";
import ViewIcon from "@/assets/svg/view-icon.svg";
import CTAmodal from "@/components/ui/CTAmodal";
import WaitlistOverlay from "@/components/ui/WaitlistOverlay";
import { useSupabaseAuth } from "@/providers/supabaseAuthProvider.client";

import ListText from "../shared/ListText";

import AddViewItem from "./AddViewItem";
import ViewMenuItem from "./ViewMenuItem";

/**
 * ViewMenu component
 * 
 * This component provides a dropdown menu for selecting views and adding new views
 * It also includes a temporary waitlist CTA button for non-logged in users
 * 
 */
const ViewMenu = ({
  selected,
  listData,
  listHandler,
  isView,
  onAddOpen,
  deleteHandler,
}) => {
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const regModalControl = useDisclosure();
  const { user } = useSupabaseAuth(); // Use Supabase auth provider
  const isLoggedIn = !!user; // User exists means logged in

  const onAddOpenHandler = () => {
    if (isLoggedIn) {
      onAddOpen();
    } else {
      setIsWaitlistOpen(true);
    }
  };

  return (
    <>
      <WaitlistOverlay
        isOpen={isWaitlistOpen}
        onClose={() => setIsWaitlistOpen(false)}
      />
      <Menu>
        {({ isOpen, onClose }) => (
          <>
            <MenuButton
              borderRadius={"4px"}
              bg={"white"}
              h={"32px"}
              w={{ base: "100%", xl: "327px" }}
              maxW={"327px"}
              px={"10px"}
              py={"8px"}
            >
              <Flex align={"center"} justify={"space-between"}>
                <Flex align={"center"} columnGap={"8px"}>
                  <Icon
                    as={isView ? ViewIcon : ListIcon}
                    w={"24px"}
                    h={"24px"}
                  />
                  <ListText title={selected?.title} />
                </Flex>
                <Icon
                  as={ArrowDownSvg}
                  transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                  transition={"all 0.4s ease-in-out"}
                />
              </Flex>
            </MenuButton>
            <MenuList
              w={{ base: "100%", sm: "327px" }}
              maxW={"327px"}
              py={"12px"}
              border={"none"}
              zIndex={"1000"}
              boxShadow={"0px 0px 20px 5px #80808040"}
            >
              <AddViewItem isView={isView} handler={onAddOpenHandler} />

              {listData?.length ? (
                <Box
                  borderBottomWidth={"1px"}
                  borderColor={"lightGray"}
                  mx={"12px"}
                  my={"8px"}
                />
              ) : null}
              <Flex direction={"column"} rowGap={"8px"}>
                {listData?.map((item, index) => (
                  <ViewMenuItem
                    key={item?.id}
                    item={item}
                    index={index}
                    onClose={onClose}
                    isView={isView}
                    isCurrent={selected?.id === item?.id}
                    icon={isView ? ViewIcon : ListIcon}
                    handler={() => listHandler(item)}
                    deleteHandler={() => deleteHandler(item)}
                  />
                ))}
              </Flex>
            </MenuList>
          </>
        )}
      </Menu>
    </>
  );
};

export default memo(ViewMenu);
